import React, { useContext } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import IOrder from "../../../models/IOrder";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwPill from "../../shared/OwPill/OwPill";
import QuoteStatusEnum from "../../../models/QuoteStatusEnum";
import SupplierService from "../../../services/SupplierService";
import * as _ from "lodash-es";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import UserService from "../../../services/UserService";
import UiContext from "../../../contexts/UiContext";
import { ClickToCopy } from "../../shared/ClickToCopy/ClickToCopy";
import OwButton from "../../shared/OwButton/OwButton";
import { SocketContext } from "@contexts/SocketContext";
import EndpointsEnum from "@models/EndpointsEnum";
import IOrderRecalculateTotalsDTO from "@models/DTO/IOrderRecalculateTotalsDTO";
import { AdminOrderActions } from "../AdminOrderActions/AdminOrderActions";
import { useAppContextNonNull } from "@contexts/AppContext";
import OrderEntryViewStateOptionsEnum from "@models/OrderEntryViewStateOptionsEnum";

type Props = {
    order?: IOrder;
    currentView: OrderEntryViewStateOptionsEnum;
};

export const OrderTitle = (props: Props) => {
    const { supplierModel, userModel, uiContext } = useAppContextNonNull();

    let isQuote = false;

    if (props.order && props.order.is_quote) {
        isQuote = true;
    }

    let isRma = false;

    if (props.currentView === OrderEntryViewStateOptionsEnum.RMA) {
        isRma = true;
    }

    let defaultPageTitle = "{order_name} " + _.startCase(SupplierService.updateStringWithOrderNameOverride("Order Entry", supplierModel));
    let defaultPageTitleId = SupplierService.updateStringWithOrderNameOverride("order_entry_with_order_name", supplierModel);

    if (isQuote) {
        defaultPageTitle = "Quote {order_name}";
        defaultPageTitleId = "quote_order_entry";
    }

    if (isRma) {
        defaultPageTitle = "{order_name} - RMA";
        defaultPageTitleId = "rma_order_entry";
    }

    let showParentOrderId = false;
    let showIsRemake = false;

    if (props.order?.parent_order_id) {
        if (!userModel.isSupplierUser && supplierModel.hide_remake_from_accounts && props.order.is_remake) {
            showParentOrderId = false;
        } else {
            showParentOrderId = true;
        }
    }

    if (props.order?.is_remake) {
        if (!userModel.isSupplierUser && supplierModel.hide_remake_from_accounts && props.order.is_remake) {
            showIsRemake = false;
        } else {
            showIsRemake = true;
        }
    }

    return (
        <div className={"OrderTitle mt-3 d-flex me-auto align-items-center"}>
            <h2>
                <span className={"fa-stack"}>
                    <i className={"fad fa-circle fa-stack-2x"} />
                    <i className="fas fa-box-open fa-xs me-2 fa-xs fa-stack-1x fa-inverse" />
                </span>
            </h2>
            <div>
                <h2 className="me-3 d-inline-flex align-items-center" data-testid={"order-entry-title"}>
                    <FormattedMessage
                        id={defaultPageTitleId}
                        defaultMessage={defaultPageTitle}
                        description="Order Entry Title"
                        values={{ order_name: props.order!.name }}
                    />
                </h2>
                {props.order?.require_payment ? (
                    <div>
                        {props.order?.is_paid ? (
                            <div className={"d-inline-flex py-0 px-2 rounded-5 bg-success text-white"}>
                                <OwLabel title={"paid"} leftIconClass={"fas fa-check-circle"} />
                            </div>
                        ) : (
                            <div className={"d-inline-flex py-0 px-2 rounded-5 bg-warning text-black"}>
                                <OwLabel title={"pending_payment"} leftIconClass={"fas fa-exclamation-triangle"} />
                            </div>
                        )}
                    </div>
                ) : null}
                {props.order?.approve_decline ? (
                    <span className={"ms-md-2"}>
                        <OwPill
                            title={props.order?.approve_decline === "APPROVE" ? "approved" : "declined"}
                            className={"ps-2 pe-2 " + (props.order.approve_decline === "APPROVE" ? "bg-success" : "bg-danger")}
                        />
                    </span>
                ) : null}
                {props.order?.is_expedited ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"expedited"} className={"ps-2 pe-2"} />
                    </span>
                ) : null}
                {showIsRemake ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"remake"} className={"ps-2 pe-2"} />
                    </span>
                ) : null}
                {props.order?.is_cancelled ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"cancelled"} className={"ps-2 pe-2"} />
                    </span>
                ) : null}
                {props.order?.is_backorder ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"backorder"} className={"ps-2 pe-2 bg-info"} leftIconClass={"fad fa-history"} />
                    </span>
                ) : null}
                {props.order?.is_on_hold ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"on_hold"} className={"ps-2 pe-2 bg-warning"} leftIconClass={"fas fa-pause"} />
                    </span>
                ) : null}
                {props.order?.is_local_delivery ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"local_delivery"} className={"ps-2 pe-2 bg-warning"} leftIconClass={"fad fa-truck"} />
                    </span>
                ) : null}
                {props.order?.is_local_pickup ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"local_pickup"} className={"ps-2 pe-2 bg-secondary"} leftIconClass={"fad fa-hand-holding-box"} />
                    </span>
                ) : null}
                {props.order?.is_quote && props.order.quote_status_id === QuoteStatusEnum.CLOSED ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"closed"} className={"ps-2 pe-2 bg-warning"} />
                    </span>
                ) : null}
                {props.order?.is_consignment ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"consignment"} className={"ps-2 pe-2 bg-primary"} leftIconClass={"fad fa-hands-holding-dollar"} />
                    </span>
                ) : null}
                {props.order?.is_dropship ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"dropship"} className={"ps-2 pe-2 bg-primary"} />
                    </span>
                ) : null}
                {props.order?.is_noninventory_order ? (
                    <span className={"ms-md-2"}>
                        <OwPill title={"non_inventory"} className={"ps-2 pe-2 bg-warning"} />
                    </span>
                ) : null}
                {showParentOrderId ? (
                    <div>
                        <OwLabel leftIconClass={"fad fa-box-alt"} title={"parent_order"} postText={":"} className={"me-2"} isBold={true} />
                        <Link
                            to={`/${props.order?.Parent_Order?.is_quote ? "quotes" : "orders"}/change/${props.order?.Parent_Order?.guid}`}>
                            {props.order?.Parent_Order?.name}
                        </Link>
                    </div>
                ) : null}
                {props.order?.is_quote && supplierModel.has_quotes && supplierModel.quote_exp_date_days && props.order.quote_exp_date ? (
                    <div>
                        <OwLabel className={"me-2"} title={"quote_exp"} postText={":"} />
                        {UserService.parseDate(props.order.quote_exp_date)}
                    </div>
                ) : null}
                {uiContext.showHiddenFeatures && props.order && (
                    <div>
                        <AdminOrderActions order={props.order} />
                    </div>
                )}
            </div>
        </div>
    );
};
